/* finger-chooser/draw.js — the draw itself: who wins, who loses, which team,
 * which place. Pure: no DOM, safe to import in node tests.
 *
 * Fairness is the whole product here. Two things decide it:
 *
 * 1. The random source is the browser's CSPRNG (crypto.getRandomValues), not
 *    Math.random. Nobody can predict it from the page state.
 * 2. Turning a 32-bit number into "one of n" uses REJECTION sampling. The
 *    naive `x % n` over-represents the low residues whenever 2^32 is not a
 *    multiple of n (for n = 3 the bias is tiny, but it is a bias, and the
 *    tests prove it is absent rather than argue it is small). Values at or
 *    above the largest multiple of n are drawn again.
 *
 * The order in which fingers touched the screen only decides their ring
 * colour. Every draw is a fresh permutation of all participants, so touching
 * first (or last, or in the middle of the screen) changes nothing.
 */

export const MODES = ['winner', 'loser', 'teams', 'order'];

/* Limits of the per-mode number. Winners/losers: at least one; teams: 2–6
 * (six team colours, each with its own shape). */
export const COUNT_RANGE = {
    winner: [1, 9],
    loser: [1, 9],
    teams: [2, 6],
    order: [0, 0],
};

export const DEFAULT_COUNT = { winner: 1, loser: 1, teams: 2, order: 0 };

const TWO_32 = 4294967296;

/* A CSPRNG stream of uint32 values, refilled 64 at a time. */
export function cryptoSource() {
    const c = (typeof globalThis !== 'undefined' && globalThis.crypto) || null;
    if (!c || typeof c.getRandomValues !== 'function') {
        throw new Error('crypto.getRandomValues is not available');
    }
    const buf = new Uint32Array(64);
    let i = buf.length;
    return function next() {
        if (i >= buf.length) { c.getRandomValues(buf); i = 0; }
        return buf[i++];
    };
}

let shared = null;
function defaultSource() {
    if (!shared) shared = cryptoSource();
    return shared();
}

/* Uniform integer in [0, n). `next` returns uint32 values (0 … 2^32−1). */
export function randomInt(n, next) {
    n = Math.floor(n);
    if (!(n >= 1)) throw new RangeError('randomInt: n must be >= 1');
    if (n === 1) return 0;
    const src = next || defaultSource;
    // The largest multiple of n that fits in 2^32; everything at or above it
    // would wrap around into the low residues and has to be redrawn.
    const limit = TWO_32 - (TWO_32 % n);
    for (let guard = 0; guard < 1000; guard++) {
        const x = src() >>> 0;
        if (x < limit) return x % n;
    }
    // Unreachable with a real source (the chance of 1000 rejections is below
    // 2^-1000); a broken test source must fail loudly, not bias silently.
    throw new Error('randomInt: random source keeps returning rejected values');
}

/* Fisher–Yates on a copy. */
export function shuffle(arr, next) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
        const j = randomInt(i + 1, next);
        const t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
}

export function clampCount(mode, count) {
    const r = COUNT_RANGE[mode] || [0, 0];
    let c = Math.round(Number(count));
    if (!Number.isFinite(c)) c = DEFAULT_COUNT[mode] || 0;
    return Math.max(r[0], Math.min(r[1], c));
}

/* How many participants a mode needs before a draw makes sense. Picking one
 * winner out of one finger is not a draw, so winners/losers need one more
 * participant than they pick; every team needs at least one member. */
export function minParticipants(mode, count) {
    const c = clampCount(mode, count);
    if (mode === 'winner' || mode === 'loser') return c + 1;
    if (mode === 'teams') return c;
    return 2;
}

/**
 * Draw a round over participants 0 … n−1.
 *
 * Returns:
 *   chosen  — participant indexes picked (winner / loser modes), in draw order
 *   teamOf  — team index per participant (teams mode), sizes differ by ≤ 1
 *   placeOf — place 1 … n per participant (order mode)
 */
export function drawRound(mode, n, count, next) {
    if (MODES.indexOf(mode) < 0) throw new Error('drawRound: unknown mode ' + mode);
    n = Math.floor(n);
    const need = minParticipants(mode, count);
    if (!(n >= need)) throw new RangeError('drawRound: ' + mode + ' needs ' + need + ' participants, got ' + n);
    const c = clampCount(mode, count);
    const idx = [];
    for (let i = 0; i < n; i++) idx.push(i);
    const perm = shuffle(idx, next);
    const out = { mode, n, count: c, chosen: [], teamOf: null, placeOf: null };

    if (mode === 'winner' || mode === 'loser') {
        out.chosen = perm.slice(0, c);
    } else if (mode === 'teams') {
        // Deal the shuffled people round-robin: sizes differ by at most one.
        // The TEAM order is shuffled too, otherwise the first colour would
        // always be the team that gets the extra player.
        const teamIdx = [];
        for (let t = 0; t < c; t++) teamIdx.push(t);
        const teamOrder = shuffle(teamIdx, next);
        out.teamOf = new Array(n);
        for (let i = 0; i < n; i++) out.teamOf[perm[i]] = teamOrder[i % c];
    } else {
        out.placeOf = new Array(n);
        for (let i = 0; i < n; i++) out.placeOf[perm[i]] = i + 1;
    }
    return out;
}

/* Team sizes of a teams result, indexed by team. */
export function teamSizes(result) {
    const sizes = new Array(result.count).fill(0);
    if (result.teamOf) for (const t of result.teamOf) sizes[t]++;
    return sizes;
}
